import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  AskAiService,
  IAskResponse,
  IChatSummary,
  IChatThreadMessage,
  ISource,
} from '../../services/ask-ai/ask-ai.service';

interface IChatMessage {
  text: string;
  sender: 'user' | 'ai';
  sources?: ISource[];
}

interface IChatThread {
  id: string;
  title: string;
  updatedAt: Date;
  messageCount: number;
  messages: IChatMessage[] | null; // null => not loaded from the server yet
  titleSynced: boolean; // true once we've picked up the AI-generated topic title
}

const TURNS_BEFORE_TOPIC_TITLE = 3;

@Component({
  selector: 'app-ai-chat',
  imports: [CommonModule, FormsModule],
  templateUrl: './ai-chat.component.html',
  styleUrl: './ai-chat.component.css',
})
export class AiChatComponent implements OnChanges {
  @ViewChild('chatMessages') private chatMessagesContainer!: ElementRef;
  @Input() aichat: boolean = false;

  @Output() toggleChatBox = new EventEmitter<any>();

  chats: IChatThread[] = [];
  activeChatId: string | null = null;

  newMessage: string = '';
  typingChatId: string | null = null;
  isLoadingChats: boolean = false;
  isLoadingActiveChat: boolean = false;
  skeletonRows = [1, 2, 3];
  downloadingSource: string | null = null;

  constructor(private askAiService: AskAiService) {}

  get activeChat(): IChatThread | undefined {
    return this.chats.find((c) => c.id === this.activeChatId) ?? undefined;
  }

  get messages(): IChatMessage[] {
    return this.activeChat?.messages ?? [];
  }

  get isTyping(): boolean {
    return this.typingChatId === this.activeChatId;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['aichat']?.currentValue && this.chats.length === 0) {
      this.loadChatList();
    }
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.scrollToBottom();
    }, 100);
  }

  scrollToBottom(): void {
    // Deferred so the DOM has been updated with the latest messages
    // (this is often called synchronously from an HTTP subscribe callback,
    // before Angular has re-rendered the message list) and scrollHeight
    // reflects the current content.
    setTimeout(() => {
      try {
        this.chatMessagesContainer.nativeElement.scrollTop =
          this.chatMessagesContainer.nativeElement.scrollHeight;
      } catch (err) {}
    });
  }

  dedupeSources(sources: ISource[] | undefined | null): ISource[] {
    if (!sources || !sources.length) return [];
    const seen = new Set<string>();
    const result: ISource[] = [];
    for (const source of sources) {
      if (!source?.label || seen.has(source.label)) continue;
      seen.add(source.label);
      result.push(source);
    }
    return result;
  }

  fileNameFromPath(path: string): string {
    if (!path) return '';
    return path.split('/').pop() || path;
  }

  downloadSource(source: ISource): void {
    const filePath = source.url || source.source_path;
    if (!filePath || this.downloadingSource === filePath) return;

    this.downloadingSource = filePath;
    this.askAiService.downloadSource(filePath).subscribe({
      next: (blob: Blob) => {
        const objectUrl = URL.createObjectURL(blob);
        window.open(objectUrl, '_blank');
        this.downloadingSource = null;
      },
      error: () => {
        this.downloadingSource = null;
      },
    });
  }

  generateThreadId(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  loadChatList(): void {
    this.isLoadingChats = true;
    this.askAiService.getChatList().subscribe({
      next: (res: IChatSummary[]) => {
        this.chats = (res || []).map((c) => ({
          id: c.thread_id,
          title: c.first_question || 'New chat',
          updatedAt: new Date(c.last_active),
          messageCount: c.message_count,
          messages: null,
          titleSynced: c.message_count >= TURNS_BEFORE_TOPIC_TITLE,
        }));
        this.isLoadingChats = false;

        if (this.chats.length) {
          this.selectChat(this.chats[0].id);
        } else {
          this.newChat();
        }
      },
      error: () => {
        this.isLoadingChats = false;
      },
    });
  }

  /**
   * After a few turns, the backend replaces `first_question` with an
   * AI-generated 3-6 word topic for the thread. Pick that up and swap it
   * in for the sidebar title (which up to now was just the raw first
   * question typed by the user).
   */
  syncChatTitle(chat: IChatThread): void {
    this.askAiService.getChatList().subscribe({
      next: (res: IChatSummary[]) => {
        const match = (res || []).find((c) => c.thread_id === chat.id);
        if (match?.first_question) {
          chat.title = match.first_question;
          chat.titleSynced = true;
        }
      },
      error: () => {}, // will retry on the next turn
    });
  }

  selectChat(chatId: string): void {
    this.activeChatId = chatId;
    const chat = this.activeChat;
    if (!chat || chat.messages !== null) return;

    this.isLoadingActiveChat = true;
    this.askAiService.getChatById(chatId).subscribe({
      next: (res: { thread_id: string; messages: IChatThreadMessage[] }) => {
        const loaded = chat;
        loaded.messages = [];
        for (const entry of res.messages || []) {
          loaded.messages.push({ text: entry.question, sender: 'user' });
          loaded.messages.push({
            text: entry.answer,
            sender: 'ai',
            sources: this.dedupeSources(entry.sources),
          });
        }
        this.isLoadingActiveChat = false;
        this.scrollToBottom();
      },
      error: () => {
        chat.messages = [];
        this.isLoadingActiveChat = false;
      },
    });
  }

  newChat(): void {
    const existingEmptyChat = this.chats.find(
      (c) => c.messages !== null && c.messages.length === 0,
    );
    if (existingEmptyChat) {
      this.selectChat(existingEmptyChat.id);
      return;
    }

    const threadId = this.generateThreadId();
    const chat: IChatThread = {
      id: threadId,
      title: 'New chat',
      updatedAt: new Date(),
      messageCount: 0,
      messages: [],
      titleSynced: false,
    };

    this.askAiService.createSession(threadId).subscribe({
      error: () => {}, // keep the chat usable locally even if session creation fails
    });

    this.chats.unshift(chat);
    this.activeChatId = chat.id;
    this.newMessage = '';
  }

  sendMessage() {
    if (this.isTyping) return;

    const text = this.newMessage.trim();
    if (!text) return;

    const chat = this.activeChat;
    if (!chat || chat.messages === null) return;

    if (chat.messages.length === 0) {
      chat.title = text.length > 40 ? text.slice(0, 40) + '...' : text;
    }

    chat.messages.push({ text, sender: 'user' });
    chat.updatedAt = new Date();
    chat.messageCount++;
    this.newMessage = '';
    this.typingChatId = chat.id;
    this.scrollToBottom();

    this.askAiService
      .askQuestion({ question: text, thread_id: chat.id })
      .subscribe({
        next: (res: IAskResponse) => {
          chat.messages!.push({
            text: res.answer,
            sender: 'ai',
            sources: this.dedupeSources(res.sources),
          });
          chat.updatedAt = new Date();
          if (this.typingChatId === chat.id) this.typingChatId = null;
          this.scrollToBottom();

          if (
            !chat.titleSynced &&
            chat.messageCount >= TURNS_BEFORE_TOPIC_TITLE
          ) {
            this.syncChatTitle(chat);
          }
        },
        error: () => {
          chat.messages!.push({
            text: 'Something went wrong. Please try again.',
            sender: 'ai',
          });
          if (this.typingChatId === chat.id) this.typingChatId = null;
          this.scrollToBottom();
        },
      });
  }

  sendMessageOnEnter(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.sendMessage();
    }
  }

  closeChatBox() {
    this.toggleChatBox.emit();
  }
}
